import React, { useEffect } from 'react';
import Chart, { Props as ApexProps } from 'react-apexcharts';
import styled from 'styled-components';

import { ChartWrapper } from '../../../components/Charts/ChartWrapper';
import { useAppSelector } from '../../../../../store';
import { useLazyGetPostsQuery } from '../service/posts.api';
import { selectAnalyticsFilters } from '../../../store/analyticFiltersSlice';

export const PostsChart = () => {
    const filters = useAppSelector(selectAnalyticsFilters);
    const disableTonality = !!filters.chat;

    const colors = ['#417ff9', '#2656c6', '#1a3c98', '#061d68'];

    const [
        getPosts,
        {
            data: messages = {
                total_messages: 0,
                posts: [],
                messages_tonality: null,
                message_statistic: [],
            },
            isFetching,
            isLoading,
        },
    ] = useLazyGetPostsQuery();

    useEffect(() => {
        if (filters) {
            const payload = {
                ...filters,
                community_id: filters.chat,
            };
            getPosts(payload, true);
        }
    }, [filters]);

    const firstDayPart = messages.message_statistic?.filter(
        el => el.hour_interval === '1'
    );
    const secondDayPart = messages.message_statistic?.filter(
        el => el.hour_interval === '2'
    );
    const thirdDayPart = messages.message_statistic?.filter(
        el => el.hour_interval === '3'
    );
    const fourthDayPart = messages.message_statistic?.filter(
        el => el.hour_interval === '4'
    );
    const fifthDayPart = messages.message_statistic?.filter(
        el => el.hour_interval === '5'
    );
    const sixthDayPart = messages.message_statistic?.filter(
        el => el.hour_interval === '6'
    );

    const dayOfWeekMap = {
        '0': 'Пн.',
        '1': 'Вт.',
        '2': 'Ср.',
        '3': 'Чт.',
        '4': 'Пт.',
        '5': 'Сб.',
        '6': 'Вс.',
    };

    const dayPart = [
        firstDayPart,
        secondDayPart,
        thirdDayPart,
        fourthDayPart,
        fifthDayPart,
        sixthDayPart,
    ].reverse();

    const intervals = {
        '0': '20:00 - 24:00',
        '1': '16:00 - 20:00',
        '2': '12:00 - 16:00',
        '3': '08:00 - 12:00',
        '4': '04:00 - 08:00',
        '5': '00:00 - 04:00',
    };

    const generateSeries = dayPart =>
        Array(7)
            .fill(null)
            .map((_, index) => {
                return {
                    x: dayOfWeekMap[index],
                    y:
                        dayPart?.find(el => +el.day_of_week === index + 1)
                            ?.count || 0,
                };
            });

    const series = dayPart.map(value => ({
        name: '',
        data: generateSeries(value),
    }));

    const options: ApexProps['options'] = {
        labels: [],
        chart: {
            toolbar: {
                show: false,
            },
        },
        legend: {
            show: false,
        },
        dataLabels: {
            enabled: false,
        },
        xaxis: {
            tooltip: {
                enabled: false,
            },
        },
        tooltip: {
            custom: function ({ series, seriesIndex, dataPointIndex }) {
                return (
                    '<div style="padding: 8px 16px">' +
                    '<span>' +
                    'Сообщений: ' +
                    '</span>' +
                    '<span>' +
                    series[seriesIndex][dataPointIndex] +
                    '</span>' +
                    '<div style="font-weight: 600">' +
                    intervals[seriesIndex] +
                    '</div>' +
                    '</div>'
                );
            },
        },
        plotOptions: {
            heatmap: {
                radius: 5,
                colorScale: {
                    ranges: [
                        {
                            from: 0,
                            to: 5,
                            color: '#94b2fa',
                            name: 'low',
                        },
                        {
                            from: 6,
                            to: 40,
                            color: '#5490f6',
                            name: 'medium',
                        },
                        {
                            from: 41,
                            color: '#083bf6',
                            name: 'high',
                        },
                    ],
                },
            },
        },
        colors: colors,
        markers: {
            colors: colors,
        },
    };

    return (
        <ChartWrapper isLoading={isFetching || isLoading} title='Сообщения'>
            <StyledContainer>
                <StyledLegendContainer>
                    <div className='counter'>{messages.total_messages}</div>
                    {disableTonality && (
                        <div>
                            <div className='result'>
                                {messages.messages_tonality}
                            </div>
                            <div className='description'>тональность</div>
                        </div>
                    )}
                </StyledLegendContainer>

                <StyledChart
                    height={'200px'}
                    type='heatmap'
                    options={options}
                    series={series}
                />
            </StyledContainer>
        </ChartWrapper>
    );
};

const StyledContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledLegendContainer = styled.div`
    height: 100%;
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .counter {
        color: #2f54eb;
        font-size: 36px;
        font-weight: 300;
    }

    .result {
        color: #139c55;
        font-size: 24px;
        font-weight: 300;
    }

    .description {
        font-size: 12px;
        font-weight: 400;
        color: rgba(37, 33, 41, 0.45);
    }
`;

const StyledChart = styled(Chart)`
    position: absolute;
    right: 0;
    top: 50%;
    height: 100%;
    transform: translateY(-50%);
`;
